import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useRequiredSession } from '../state/session';
import { authUsable, useSyncUi } from '../state/sync';
import { useSaves } from '../state/saves';
import { flushAll } from '../state/saves';

const fmtTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) : '';

/**
 * 머리 영역의 동기화 상태 + 자동 동기화.
 * 연결되어 있고 토큰이 유효하면: 잠금 해제 직후, 입력 저장이 끝난 뒤(15초 간격), 온라인 복귀 시, 3분마다 동기화한다.
 */
export function SyncBadge() {
  const session = useRequiredSession();
  const ui = useSyncUi();
  const pendingSaves = useSaves((s) => s.pending);

  useEffect(() => {
    void ui.refresh(session);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => {
    if (!ui.connected) return;
    let last = 0;
    const tick = async (force = false) => {
      if (!authUsable() || !navigator.onLine || document.visibilityState !== 'visible') return;
      if (useSaves.getState().pending > 0) return;
      const pending = await session.db.outbox.count();
      if (force || pending > 0 || Date.now() - last > 180_000) {
        last = Date.now();
        await flushAll();
        await useSyncUi.getState().syncNow(session);
      }
    };
    void tick(true);
    const timer = window.setInterval(() => void tick(), 15_000);
    const online = () => void tick(true);
    window.addEventListener('online', online);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', online);
    };
  }, [ui.connected, session]);

  let text: string;
  let cls = 'sync-badge';
  if (!ui.connected) {
    text = 'Drive 연결 안 됨';
  } else if (ui.running) {
    text = '동기화 중…';
  } else if (!ui.tokenValid) {
    text = 'Drive 다시 연결 필요';
    cls += ' warn';
  } else if (ui.error) {
    text = navigator.onLine ? '동기화 오류' : '오프라인 · 이 기기에 저장됨';
    cls += ' warn';
  } else {
    text = ui.lastSyncAt ? `동기화됨 ${fmtTime(ui.lastSyncAt)}` : '동기화 대기';
    if (pendingSaves > 0 || ui.pending > 0) text += ' · 변경 있음';
  }

  return (
    <span className={cls}>
      {ui.conflicts > 0 && (
        <Link to="/settings" className="badge badge-review">
          충돌 {ui.conflicts}
        </Link>
      )}
      {ui.connected && !ui.tokenValid && !ui.running ? (
        <button type="button" className="link" onClick={() => void ui.connect(session)}>
          {text}
        </button>
      ) : (
        <Link to="/settings" className="muted small">
          {text}
        </Link>
      )}
    </span>
  );
}
